import { useEffect, useRef, useState } from "react";
import { useLocation, useRoute } from "wouter";
import {
  Trash2,
  Bell,
  Check,
  CircleCheckBig,
  CircleDot,
  X,
  Clock,
  CopyPlus,
  ChevronRight,
  Logs,
  StickyNote,
  Timer,
  Repeat2,
} from "lucide-react";
import AppShell from "@/components/foundations/core/AppShell/AppShell";
import { TimeInputField } from "@/components/foundations/inputs/TimeInputField/TimeInputField";
import ListSelector, {
  type ListOption,
} from "@/components/foundations/lists/ListSelector/ListSelector";
import { DatePickerInput } from "@/components/foundations/inputs/DatePickerInput/DatePickerInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { trpc } from "@/lib/trpc";
import { findSmartBackTarget } from "@/lib/navigationHistory";
import {
  DEFAULT_AREA_COLOR,
  AREA_COLORS,
  ListColorKey,
  resolveTaskColors,
} from "@/lib/listColors";
import {
  getPrimaryActionStyle,
  getSecondaryActionStyle,
  resolveDisplayTheme,
} from "@/lib/displayTheme";
import { type TaskFieldsValue } from "@/components/tasks/details/TaskFieldsForm/TaskFieldsForm";
import TaskDetailsHeader from "@/components/tasks/details/TaskDetailsHeader/TaskDetailsHeader";
import TaskInfoSection from "@/components/tasks/details/TaskInfoSection/TaskInfoSection";
import TaskFieldSection from "@/components/tasks/details/TaskFieldSection/TaskFieldSection";
import TaskStatusForm from "@/components/tasks/status/TaskStatusForm/TaskStatusForm";
import TaskNoteEditor from "@/components/tasks/details/TaskNoteEditor/TaskNoteEditor";
import {
  countTaskLogLines,
  TaskLogEditor,
} from "@/components/tasks/details/TaskLogEditor/TaskLogEditor";
import TaskScheduleForm from "@/components/tasks/schedule/TaskScheduleForm/TaskScheduleForm";
import RepeatOptions from "@/components/tasks/repeat/RepeatOptions/RepeatOptions";
import {
  repeatSummary,
  type RepeatRule,
} from "@/components/tasks/repeat/RepeatOptions/repeat-options-types";
import { createTaskScheduleValue } from "@/lib/taskTiming";
import { formatDateKey, parseDateKey, roundTimeStringToStep } from "@/lib/time";
import {
  combineDateAndTime,
  dateAndTimeToHHMM,
  ResultField,
  resultDurationMinutes,
} from "@/pages/TaskResults";
import { toast } from "sonner";

type Status = "pending" | "done" | "skipped";

function minutesToHHMM(min: number | null | undefined) {
  if (min === null || min === undefined) return "";
  const h = Math.floor(min / 60)
    .toString()
    .padStart(2, "0");
  const m = (min % 60).toString().padStart(2, "0");
  return roundTimeStringToStep(`${h}:${m}`);
}
function addDays(dateKey: string, days: number): string {
  const d = parseDateKey(dateKey);
  d.setDate(d.getDate() + days);
  return formatDateKey(d);
}

function todayDateKey() {
  return formatDateKey(new Date());
}

export default function TaskDetail() {
  const [, params] = useRoute<{ id: string }>("/task/:id");
  const [location, setLocation] = useLocation();
  const id = params ? parseInt(params.id, 10) : 0;
  const utils = trpc.useUtils();

  const taskQuery = trpc.tasks.get.useQuery({ id }, { enabled: id > 0 });
  const listsQuery = trpc.lists.list.useQuery();
  const repeatQuery = trpc.tasks.getRepeatSeries.useQuery(
    { id },
    { enabled: id > 0 }
  );

  // Single value object that drives the shared form.
  const [form, setForm] = useState<TaskFieldsValue>({
    title: "",
    details: "",
    notes: "",
    startTime: "",
    durationMin: "",
    alertEnabled: true,
    priority: null,
    customColors: false,
    barColor: null,
    bgColor: null,
  });
  const [duplicateSheetOpen, setDuplicateSheetOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(true);
  const [statusOpen, setStatusOpen] = useState(false);
  const [repeatOpen, setRepeatOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [repeatRule, setRepeatRule] = useState<RepeatRule>({ kind: "none" });
  const [savedRepeatRule, setSavedRepeatRule] = useState<RepeatRule>({
    kind: "none",
  });
  const [excludedOccurrenceKeys, setExcludedOccurrenceKeys] = useState<
    string[]
  >([]);
  const [savedExcludedOccurrenceKeys, setSavedExcludedOccurrenceKeys] =
    useState<string[]>([]);
  const [startedAtTime, setStartedAtTime] = useState("");
  const [endedAtTime, setEndedAtTime] = useState("");
  const [resultDuration, setResultDuration] = useState("");
  const [statusDate, setStatusDate] = useState("");
  const [statusEndDate, setStatusEndDate] = useState("");
  const [duplicateDate, setDuplicateDate] = useState("");
  const [duplicateTime, setDuplicateTime] = useState("");
  const [duplicateDurationMin, setDuplicateDurationMin] = useState("");
  const [duplicateAlertEnabled, setDuplicateAlertEnabled] = useState(true);
  const [areaPickerOpen, setAreaPickerOpen] = useState(false);
  const [logText, setLogText] = useState("");
  const savedLogRef = useRef("");

  // Hydrate from server.
  useEffect(() => {
    if (!taskQuery.data) return;
    const t = taskQuery.data;
    const tBar = (t.barColor ?? null) as ListColorKey | null;
    const tBg = (t.bgColor ?? null) as ListColorKey | null;
    setForm({
      title: t.title,
      details: t.details ?? "",
      notes: t.notes ?? "",
      startTime: minutesToHHMM(t.startMinutes),
      durationMin: t.durationMin?.toString() ?? "",
      priority: t.priority ?? null,
      alertEnabled: t.alertEnabled,
      customColors: tBar !== null || tBg !== null,
      barColor: tBar,
      bgColor: tBg,
    });
    setLogText(t.log ?? "");
    savedLogRef.current = t.log ?? "";
    setDuplicateDate(t.date ? addDays(t.date, 1) : todayDateKey());
    setDuplicateTime(minutesToHHMM(t.startMinutes));
    setDuplicateDurationMin(t.durationMin?.toString() ?? "");
    setDuplicateAlertEnabled(t.alertEnabled);
    const startedAt = t.startedAt as unknown as Date | null;
    const completedAt = t.completedAt as unknown as Date | null;
    setStartedAtTime(dateAndTimeToHHMM(startedAt));
    setEndedAtTime(dateAndTimeToHHMM(completedAt));
    setResultDuration(resultDurationMinutes(startedAt, completedAt));
    setStatusDate(
      startedAt
        ? formatDateKey(new Date(startedAt))
        : completedAt
          ? formatDateKey(new Date(completedAt))
          : ""
    );
    setStatusEndDate(
      completedAt
        ? formatDateKey(new Date(completedAt))
        : startedAt
          ? formatDateKey(new Date(startedAt))
          : ""
    );
  }, [taskQuery.data]);

  useEffect(() => {
    if (repeatQuery.data === undefined) return;
    const rule = (repeatQuery.data?.series.rule ?? {
      kind: "none",
    }) as RepeatRule;
    const excluded =
      repeatQuery.data?.occurrences
        .filter(occurrence => !occurrence.enabled)
        .map(occurrence => occurrence.occurrenceKey) ?? [];
    setRepeatRule(rule);
    setSavedRepeatRule(rule);
    setExcludedOccurrenceKeys(excluded);
    setSavedExcludedOccurrenceKeys(excluded);
  }, [repeatQuery.data]);

  const updateMutation = trpc.tasks.update.useMutation({
    onSuccess: () => {
      utils.tasks.get.invalidate({ id });
      utils.tasks.listInbox.invalidate();
      utils.day.list.invalidate();
      utils.day.monthSummary.invalidate();
    },
    onError: e => toast.error(e.message),
  });
  const saveRepeatMutation = trpc.tasks.saveRepeatSeries.useMutation({
    onSuccess: result => {
      setSavedRepeatRule(result.rule as RepeatRule);
      const excluded = result.occurrences
        .filter(occurrence => !occurrence.enabled)
        .map(occurrence => occurrence.occurrenceKey);
      setExcludedOccurrenceKeys(excluded);
      setSavedExcludedOccurrenceKeys(excluded);
      void repeatQuery.refetch();
      void utils.tasks.get.invalidate({ id });
      void utils.day.list.invalidate();
      void utils.day.monthSummary.invalidate();
      toast.success("Repeat schedule saved");
    },
    onError: error => toast.error(error.message),
  });

  const markDoneMutation = trpc.tasks.markDone.useMutation({
    onSuccess: () => {
      utils.tasks.get.invalidate({ id });
      utils.tasks.listInbox.invalidate();
      utils.day.list.invalidate();
      utils.day.monthSummary.invalidate();
    },
    onError: e => toast.error(e.message),
  });

  const removeMutation = trpc.tasks.remove.useMutation({
    onSuccess: () => {
      utils.day.list.invalidate();
      utils.tasks.listInbox.invalidate();
      utils.day.monthSummary.invalidate();
      toast.success("Task deleted");
      if (taskQuery.data?.date)
        setLocation(`/calendar?day=${taskQuery.data.date}`);
      else if (taskQuery.data) setLocation("/inbox");
      else setLocation("/");
    },
  });

  const duplicateMutation = trpc.tasks.duplicate.useMutation({
    onSuccess: (_data, vars) => {
      setDuplicateSheetOpen(false);
      if (vars.date) utils.day.list.invalidate({ date: vars.date });
      else utils.tasks.listInbox.invalidate();
      utils.day.monthSummary.invalidate();
      toast.success(
        "Task duplicated",
        vars.date
          ? {
              action: {
                label: "View day",
                onClick: () => setLocation(`/calendar?day=${vars.date}`),
              },
            }
          : undefined
      );
    },
    onError: e => toast.error(e.message),
  });

  function patch(fields: Record<string, unknown>) {
    updateMutation.mutate({ id, ...fields });
  }

  function saveLog() {
    const next = logText.trim() === "" ? null : logText;
    const current =
      savedLogRef.current.trim() === "" ? null : savedLogRef.current;
    if (next === current) return;
    updateMutation.mutate({
      id,
      log: next,
      clientTimestamp: new Date().toISOString(),
    });
    savedLogRef.current = logText;
  }

  const resultDate = statusDate;
  const resultDateForTime = resultDate || formatDateKey(new Date());

  function saveDuration() {
    if (!startedAtTime) return;
    const raw = resultDuration.trim();
    if (raw === "") return;
    const minutes = Math.max(0, parseInt(raw, 10) || 0);
    const startedAt = combineDateAndTime(resultDateForTime, startedAtTime);
    const completedAt = new Date(startedAt.getTime() + minutes * 60000);
    setEndedAtTime(dateAndTimeToHHMM(completedAt));
    setStatusEndDate(formatDateKey(completedAt));
    updateMutation.mutate({
      id,
      completedAt,
      clientTimestamp: new Date().toISOString(),
    });
  }

  function goBack() {
    const target = findSmartBackTarget(
      location,
      path => !path.startsWith("/task/")
    );
    if (target) setLocation(target);
    else if (taskQuery.data?.date)
      setLocation(`/calendar?day=${taskQuery.data.date}`);
    else setLocation("/inbox");
  }

  if (!id) return null;
  if (taskQuery.isLoading || !taskQuery.data) {
    return (
      <AppShell onBack={goBack} title="Task">
        <div className="container max-w-2xl py-8">
          <div className="h-40 rounded-xl bg-muted/40 animate-pulse" />
        </div>
      </AppShell>
    );
  }

  const t = taskQuery.data;
  const status = (t.status as Status) ?? "pending";
  const localStartedAt = startedAtTime
    ? combineDateAndTime(resultDateForTime, startedAtTime)
    : null;
  const localCompletedAt = endedAtTime
    ? combineDateAndTime(statusEndDate || resultDateForTime, endedAtTime)
    : null;
  const statusColors = resolveTaskColors({
    barColor: form.customColors ? form.barColor : null,
    bgColor: form.customColors ? form.bgColor : null,
    listColor: (t.listColor as string | null) ?? DEFAULT_AREA_COLOR,
    listBgColor: (t as { listBgColor?: string | null }).listBgColor ?? null,
  });
  const listColors = resolveTaskColors({
    barColor: null,
    bgColor: null,
    listColor: (t.listColor as string | null) ?? DEFAULT_AREA_COLOR,
    listBgColor: (t as { listBgColor?: string | null }).listBgColor ?? null,
  });
  const displayTheme = resolveDisplayTheme({
    primaryColor: form.customColors ? form.barColor : null,
    secondaryColor: form.customColors ? form.bgColor : null,
    fallbackPrimary: (t.listColor as string | null) ?? DEFAULT_AREA_COLOR,
    fallbackSecondary:
      (t as { listBgColor?: string | null }).listBgColor ?? null,
  });
  const currentTaskAnchor = `${t.date ?? todayDateKey()}T${form.startTime || "00:00"}`;
  const repeatSourceTaskId = repeatQuery.data?.series.sourceTaskId ?? id;
  const repeatAnchorDateTime =
    repeatSourceTaskId === id
      ? currentTaskAnchor
      : (repeatQuery.data?.series.anchorDateTime ?? currentTaskAnchor);

  // Translates a partial form change into the tRPC payload, normalizing the
  // empty/null cases that fix the "can't clear a field" bug.
  function commit(partial: Partial<TaskFieldsValue>) {
    const payload: Record<string, unknown> = {};
    if ("title" in partial) {
      const next = (partial.title ?? "").trim();
      if (next === "") return; // title is required; ignore clear
      if (next !== t.title) payload.title = next;
    }
    if ("details" in partial) {
      const trimmed = (partial.details ?? "").trim();
      const next = trimmed === "" ? null : trimmed;
      if ((next ?? null) !== (t.details ?? null)) payload.details = next;
    }
    if ("notes" in partial) {
      const trimmed = (partial.notes ?? "").trim();
      const next = trimmed === "" ? null : trimmed;
      if ((next ?? null) !== (t.notes ?? null)) payload.notes = next;
    }
    if ("startTime" in partial) {
      const next = partial.startTime ?? "";
      if (next !== minutesToHHMM(t.startMinutes)) {
        payload.startTime = next || null;
      }
    }
    if ("durationMin" in partial) {
      const raw = (partial.durationMin ?? "").trim();
      const parsed = raw === "" ? null : Number.parseInt(raw, 10);
      const next =
        parsed === null || !Number.isFinite(parsed) || parsed <= 0
          ? null
          : parsed;
      if (next !== (t.durationMin ?? null)) payload.durationMin = next;
    }
    if ("alertEnabled" in partial) {
      if (partial.alertEnabled !== t.alertEnabled) {
        payload.alertEnabled = partial.alertEnabled;
      }
    }
    if (
      "customColors" in partial ||
      "barColor" in partial ||
      "bgColor" in partial
    ) {
      const customOn = partial.customColors ?? form.customColors;
      const nextBar = customOn
        ? "barColor" in partial
          ? (partial.barColor ?? null)
          : (form.barColor ?? null)
        : null;
      const nextBg = customOn
        ? "bgColor" in partial
          ? (partial.bgColor ?? null)
          : (form.bgColor ?? null)
        : null;
      if ((nextBar ?? null) !== (t.barColor ?? null))
        payload.barColor = nextBar;
      if ((nextBg ?? null) !== (t.bgColor ?? null)) payload.bgColor = nextBg;
    }
    if (Object.keys(payload).length > 0) patch(payload);
  }

  function updateDisplayDraft(nextDraft: {
    customColors: boolean;
    barColor: ListColorKey | null;
    bgColor: ListColorKey | null;
  }) {
    setForm(current => ({
      ...current,
      customColors: nextDraft.customColors,
      barColor: nextDraft.barColor,
      bgColor: nextDraft.bgColor,
    }));
    commit({
      customColors: nextDraft.customColors,
      barColor: nextDraft.barColor,
      bgColor: nextDraft.bgColor,
    });
  }

  return (
    <AppShell onBack={goBack} title={t.title}>
      <div className="ui-main-container container max-w-2xl py-4 space-y-2 pb-8 sm:py-6 sm:space-y-6 sm:pb-12">
        <TaskDetailsHeader
          listName={t.listName ?? "Inbox"}
          colors={listColors}
          onAreaClick={() => setAreaPickerOpen(true)}
          onOptionsClick={() => setLocation(`/task/${id}/display`)}
        />
        <TaskInfoSection
          value={{ title: form.title, description: form.details }}
          savedValue={{ title: t.title, description: t.details ?? "" }}
          colors={statusColors}
          onChange={(next, reason) => {
            setForm(current => ({
              ...current,
              ...(reason === "title"
                ? { title: next.title }
                : { details: next.description }),
            }));
          }}
          onCommit={(next, reason) => {
            if (reason === "title") commit({ title: next.title });
            else commit({ details: next.description });
          }}
          onDisplayClick={() => setLocation(`/task/${id}/display`)}
        />
        {areaPickerOpen && (
          <ListSelector
            lists={(listsQuery.data ?? []) as ListOption[]}
            value={t.listId}
            onChange={listId => {
              if (listId === t.listId) return;
              if (listId !== null)
                localStorage.setItem("lastUsedListId", String(listId));
              updateMutation.mutate(
                { id, listId, clientTimestamp: new Date().toISOString() },
                {
                  onSuccess: () => {
                    const nextArea = (listsQuery.data ?? []).find(
                      list => list.id === listId
                    );
                    utils.tasks.get.setData({ id }, current =>
                      current
                        ? {
                            ...current,
                            listId,
                            listName: nextArea?.name ?? current.listName,
                            listColor: nextArea?.color ?? current.listColor,
                            listBgColor:
                              nextArea?.bgColor ?? current.listBgColor,
                            listKind: nextArea?.kind ?? current.listKind,
                          }
                        : current
                    );
                    setAreaPickerOpen(false);
                  },
                }
              );
            }}
            onClose={() => setAreaPickerOpen(false)}
            title="Move task to list"
          />
        )}

        <TaskScheduleForm
          open={scheduleOpen}
          onOpenChange={setScheduleOpen}
          value={createTaskScheduleValue({
            date: t.date ?? "",
            startTime: form.startTime,
            durationMinutes: form.durationMin,
            alertEnabled: form.alertEnabled,
          })}
          onChange={(next, reason) => {
            setForm(current => ({
              ...current,
              startTime: next.timing.startTime,
              durationMin: next.timing.durationMinutes,
              alertEnabled: next.alertEnabled,
            }));
            if (reason === "duration") return;
            patch({
              date: next.timing.startDate || null,
              startTime: next.timing.startTime || null,
              durationMin: next.timing.durationMinutes || null,
              alertEnabled: next.alertEnabled,
              clientTimestamp: new Date().toISOString(),
            });
          }}
          onCommit={next =>
            commit({ durationMin: next.timing.durationMinutes })
          }
          colors={statusColors}
          emptyLabel="No schedule"
        />
        <TaskStatusForm
          value={{
            status,
            startedAt: localStartedAt,
            completedAt: localCompletedAt,
            timing: {
              startDate: resultDate,
              endDate: statusEndDate || resultDate,
              startTime: startedAtTime,
              endTime: endedAtTime,
              durationMinutes: resultDuration,
            },
          }}
          schedule={createTaskScheduleValue({
            date: t.date ?? "",
            startTime: form.startTime,
            durationMinutes: form.durationMin,
            alertEnabled: form.alertEnabled,
          })}
          colors={statusColors}
          open={statusOpen}
          onOpenChange={setStatusOpen}
          onChange={(next, reason) => {
            setStatusDate(next.timing.startDate);
            setStatusEndDate(next.timing.endDate);
            setStartedAtTime(next.timing.startTime);
            setEndedAtTime(next.timing.endTime);
            setResultDuration(next.timing.durationMinutes);
            utils.tasks.get.setData({ id }, current =>
              current
                ? {
                    ...current,
                    status: next.status,
                    startedAt: next.startedAt,
                    completedAt: next.completedAt,
                  }
                : current
            );

            if (reason === "duration") return;
            const timingOnlyReason =
              reason === "start-date" ||
              reason === "end-date" ||
              reason === "start-time" ||
              reason === "end-time";
            if (timingOnlyReason) {
              updateMutation.mutate({
                id,
                startedAt: next.startedAt,
                completedAt: next.completedAt,
                clientTimestamp: new Date().toISOString(),
              });
              return;
            }
            if (reason === "status" && next.status === "done") {
              const timestamp =
                next.completedAt ?? next.startedAt ?? new Date();
              markDoneMutation.mutate({
                id,
                clientTimestamp: timestamp.toISOString(),
              });
              return;
            }
            updateMutation.mutate({
              id,
              status: next.status,
              startedAt: next.startedAt,
              completedAt: next.completedAt,
              clientTimestamp: new Date().toISOString(),
            });
          }}
          onCommit={saveDuration}
        />

        <TaskFieldSection
          icon={<Repeat2 />}
          title="Repeat"
          summary={repeatSummary(repeatRule)}
          open={repeatOpen}
          onOpenChange={setRepeatOpen}
          colors={statusColors}
        >
          <RepeatOptions
            value={repeatRule}
            onChange={setRepeatRule}
            savedValue={savedRepeatRule}
            excludedOccurrenceKeys={excludedOccurrenceKeys}
            savedExcludedOccurrenceKeys={savedExcludedOccurrenceKeys}
            onExcludedOccurrenceKeysChange={setExcludedOccurrenceKeys}
            anchor={{
              dateTime: repeatAnchorDateTime,
              alarm:
                repeatQuery.data?.sourceTask?.alertEnabled ?? form.alertEnabled,
              duration:
                repeatQuery.data?.sourceTask?.durationMin ??
                (form.durationMin ? Number(form.durationMin) : undefined),
              href: `/task/${repeatSourceTaskId}`,
            }}
            savedAnchorDateTime={repeatQuery.data?.series.anchorDateTime}
            isSaving={saveRepeatMutation.isPending}
            onSave={(rule, excludedKeys) =>
              saveRepeatMutation.mutate({
                id,
                rule,
                excludedOccurrenceKeys: excludedKeys,
                maxOccurrences: 20,
              })
            }
          />
        </TaskFieldSection>

        <TaskFieldSection
          icon={<StickyNote />}
          title="Notes"
          summary={form.notes || "Add note"}
          open={notesOpen}
          onOpenChange={setNotesOpen}
          colors={statusColors}
        >
          <TaskNoteEditor
            savedValue={t.notes ?? ""}
            onSave={notes => {
              setForm(current => ({ ...current, notes }));
              commit({ notes });
            }}
            isSaving={updateMutation.isPending}
            colors={statusColors}
          />
        </TaskFieldSection>

        <TaskFieldSection
          icon={<Logs />}
          title="Event log"
          summary={`${countTaskLogLines(logText)} events`}
          open={logOpen}
          onOpenChange={setLogOpen}
          colors={statusColors}
        >
          <TaskLogEditor
            value={logText}
            savedValue={savedLogRef.current}
            onChange={setLogText}
            onClear={() => setLogText("")}
            onCancel={() => setLogText(savedLogRef.current)}
            onSave={saveLog}
            isSaving={updateMutation.isPending}
            accent={statusColors}
            headerIcon={<Logs className="h-3.5 w-3.5" />}
          />
        </TaskFieldSection>

        <div className="ui-button-group pt-2 flex gap-3">
          <Button
            variant="ghost"
            className="flex-1 h-12 text-destructive hover:text-destructive bg-destructive/10 hover:bg-destructive/15 gap-2 font-medium"
            onClick={() => removeMutation.mutate({ ids: [id] })}
            disabled={removeMutation.isPending}
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
          <Button
            type="button"
            className="flex-1 h-12 text-base font-medium gap-2"
            onClick={() => setDuplicateSheetOpen(true)}
            style={getSecondaryActionStyle(displayTheme)}
          >
            <CopyPlus className="h-4 w-4" />
            Duplicate
          </Button>
        </div>

        <div className="ui-close-button pt-5 pb-safe">
          <Button
            variant="default"
            className="w-full h-14 text-base font-medium gap-2"
            style={getPrimaryActionStyle(displayTheme)}
            onClick={goBack}
          >
            <X className="h-4 w-4" />
            Close
          </Button>
        </div>

        <Sheet open={duplicateSheetOpen} onOpenChange={setDuplicateSheetOpen}>
          <SheetContent side="bottom" className="rounded-t-2xl">
            <div className="mx-auto w-full max-w-2xl">
              <SheetHeader>
                <SheetTitle style={{ color: statusColors.ink }}>
                  Duplicate task
                </SheetTitle>
                <SheetDescription
                  style={{ color: statusColors.ink, opacity: 0.68 }}
                >
                  Create one pending copy.
                </SheetDescription>
              </SheetHeader>
              <div className="px-4 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2 rounded-xl border border-border/60 p-3">
                    <Label
                      className="text-[11px] uppercase tracking-wider text-muted-foreground"
                      style={{ color: statusColors.ink, opacity: 0.68 }}
                    >
                      Date
                    </Label>
                    <DatePickerInput
                      value={duplicateDate}
                      onChange={setDuplicateDate}
                      style={{
                        color: statusColors.ink,
                        borderColor: `color-mix(in oklch, ${statusColors.bar} 22%, transparent)`,
                      }}
                    />
                  </div>
                  <div className="space-y-2 rounded-xl border border-border/60 p-3">
                    <Label
                      className="text-[11px] uppercase tracking-wider text-muted-foreground"
                      style={{ color: statusColors.ink, opacity: 0.68 }}
                    >
                      Time
                    </Label>
                    <TimeInputField
                      value={duplicateTime}
                      onChange={e => setDuplicateTime(e)}
                      borderColor={statusColors.bar}
                      style={{
                        color: statusColors.ink,
                        borderColor: `color-mix(in oklch, ${statusColors.bar} 22%, transparent)`,
                      }}
                    />
                  </div>
                  <div className="space-y-2 rounded-xl border border-border/60 p-3">
                    <Label
                      className="text-[11px] uppercase tracking-wider text-muted-foreground"
                      style={{ color: statusColors.ink, opacity: 0.68 }}
                    >
                      Duration
                    </Label>
                    <Input
                      type="number"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      min={1}
                      value={duplicateDurationMin}
                      placeholder="optional"
                      onChange={e => setDuplicateDurationMin(e.target.value)}
                      style={{
                        color: statusColors.ink,
                        borderColor: `color-mix(in oklch, ${statusColors.bar} 22%, transparent)`,
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setDuplicateAlertEnabled(v => !v)}
                    className="space-y-2 rounded-xl border border-border/60 p-3 text-left transition-opacity hover:opacity-80"
                    style={{
                      color: statusColors.ink,
                      borderColor: `color-mix(in oklch, ${statusColors.bar} 22%, transparent)`,
                    }}
                  >
                    <div
                      className="text-[11px] uppercase tracking-wider text-muted-foreground"
                      style={{ color: statusColors.ink, opacity: 0.68 }}
                    >
                      Alert
                    </div>
                    <div className="flex h-9 items-center gap-1.5 font-display text-base">
                      <Bell
                        className="h-4 w-4"
                        color={statusColors.bar}
                        fill={duplicateAlertEnabled ? statusColors.bar : "none"}
                      />
                      {duplicateAlertEnabled ? "On" : "Off"}
                    </div>
                  </button>
                </div>
              </div>
              <SheetFooter>
                <Button
                  type="button"
                  disabled={!duplicateDate || duplicateMutation.isPending}
                  onClick={() => {
                    duplicateMutation.mutate({
                      id,
                      date: duplicateDate,
                      startTime: duplicateTime || null,
                      durationMin:
                        duplicateDurationMin.trim() === ""
                          ? null
                          : Math.max(
                              1,
                              parseInt(duplicateDurationMin, 10) || 1
                            ),
                      alertEnabled: duplicateAlertEnabled,
                    });
                  }}
                  style={getPrimaryActionStyle(displayTheme)}
                >
                  Duplicate
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDuplicateSheetOpen(false)}
                  style={{
                    color: statusColors.ink,
                    borderColor: `color-mix(in oklch, ${statusColors.bar} 22%, transparent)`,
                  }}
                >
                  Cancel
                </Button>
              </SheetFooter>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </AppShell>
  );
}

function StatusButton({
  icon,
  label,
  active,
  onClick,
  colors,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
  colors: { bar: string; soft: string; ink: string };
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-10 rounded-lg text-sm font-medium transition-all inline-flex items-center justify-center gap-1.5"
      style={{
        background: active
          ? colors.bar
          : `color-mix(in oklch, ${colors.bar} 10%, transparent)`,
        color: active ? colors.soft : colors.ink,
      }}
    >
      {icon}
      {label}
    </button>
  );
}
